import React, { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import { PillButton, SurfaceCard } from "../../components/ui";
import GuideLink from "../../components/guides/GuideLink";
import { RegistrationDraftStore } from "../../store/store";
import { registrationRecords, RegistrationResult } from "../../API/firebase";
import { describeFirebaseError } from "../../lib/firebase-errors";
import { paths } from "../paths";

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle2" sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function SummaryStep() {
  const navigate = useNavigate();
  const draft = RegistrationDraftStore((state) => state);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RegistrationResult | null>(null);

  // The step guard in RegisterLayout guarantees these are all present.
  const { artomeSerialNo, epsonSerialNo, productName, ownerCompanyName } = draft;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const outcome = await registrationRecords(
        epsonSerialNo,
        artomeSerialNo,
        productName,
        ownerCompanyName
      );
      setResult(outcome);
    } catch (err) {
      // Logged raw as well as described: the console copy is what support asks
      // for, and it keeps the stack that the message deliberately drops.
      console.error("Registration save failed", err);
      setError(describeFirebaseError(err, "The registration"));
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <SurfaceCard>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <CheckCircleIcon sx={{ color: "primary.main" }} />
          <Typography variant="h4">
            {result.mode === "created"
              ? "Registration complete"
              : "Registration updated"}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {productName} {artomeSerialNo} is registered to {ownerCompanyName}.
        </Typography>

        {result.previousOwner && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This device was previously registered to{" "}
            <strong>{result.previousOwner}</strong>. That owner has been recorded
            in the device's history.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Next steps
        </Typography>
        <Box sx={{ mb: 3 }}>
          <GuideLink product={productName} variant="block" />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <PillButton
            tone="amber"
            withArrow
            onClick={() => {
              draft.reset();
              navigate(paths.registerSerial);
            }}
          >
            Register another product
          </PillButton>
          <PillButton
            tone="outline"
            onClick={() => {
              draft.reset();
              navigate(paths.home);
            }}
          >
            Done
          </PillButton>
        </Box>
      </SurfaceCard>
    );
  }

  return (
    <SurfaceCard>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Check the details
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        This registers the warranty for the owner organization below.
      </Typography>

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          px: 2,
          py: 1,
          mb: 3,
        }}
      >
        <SummaryRow label="Product" value={productName} />
        <Divider />
        <SummaryRow label="Artome serial" value={artomeSerialNo} />
        <Divider />
        <SummaryRow label="Projector serial" value={epsonSerialNo} />
        <Divider />
        <SummaryRow label="Owner" value={ownerCompanyName} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
        <PillButton
          tone="outline"
          disabled={saving}
          onClick={() => navigate(paths.registerOwner)}
        >
          Back
        </PillButton>
        <PillButton tone="amber" disabled={saving} onClick={save}>
          {saving ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            "Save registration"
          )}
        </PillButton>
      </Box>
    </SurfaceCard>
  );
}
